import { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    AreaChart,
    Area,
} from "recharts";
import { PageHeader } from "../components/PageHeader";

type TimeRange = "24h" | "7d" | "30d" | "90d" | "all";

interface DashboardStats {
    totalUsers: number;
    activeUsers: number;
    newUsers: number;
    freeUsers: number;
    proUsers: number;
    premiumUsers: number;
    totalPurchases: number;
    totalRevenue: number;
    activeCampaigns: number;
    activePromoCodes: number;
    totalTokensUsed: number;
    monthlyTokensUsed: number;
    // Referral stats
    totalReferrals: number;
    completedReferrals: number;
    totalTextCreditsGiven: number;
    totalVoiceCreditsGiven: number;
    // Business Metrics
    ltv: number;
    arppu: number;
    rangePayersCount: number;
    subscriptionRevenue: number;
    boosterRevenue: number;
    retentionRate: number;
    churnRiskCount: number;
    avgConversionDays: number;
    // Paywall Metrics
    paywallViews: number;
    paywallTapsUpgrade: number;
    paywallTapsBooster: number;
    paywallConversionRate: number;
    // Trends (% change from previous period)
    newUsersTrend: number;
    revenueTrend: number;
    activeUsersTrend: number;
    referralsTrend: number;
}

interface ChartDataPoint {
    date: string;
    count: number;
}

interface RevenueData {
    name: string;
    revenue: number;
    count: number;
}

const COLORS = {
    free: "#94a3b8",
    pro: "#3b82f6",
    premium: "#8b5cf6",
    text: "#10b981",
    voice: "#f59e0b",
    chart: ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"],
};

export function AnalyticsDashboard() {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [timeRange, setTimeRange] = useState<TimeRange>("7d");
    const [growthData, setGrowthData] = useState<ChartDataPoint[]>([]);
    const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
    const [loading, setLoading] = useState(true);
    const [geminiCost, setGeminiCost] = useState(0.075); // Default fallback

    useEffect(() => {
        loadDashboardData();
    }, [timeRange]);

    const parseDate = (d: any): Date => {
        if (!d) return new Date(0);
        if (d instanceof Date) return d;
        if (typeof d.toDate === "function") return d.toDate();
        if (d.seconds) return new Date(d.seconds * 1000);
        return new Date(d);
    };

    async function loadDashboardData() {
        setLoading(true);
        try {
            const firestore = getFirestore();

            // Load Gemini cost from config
            try {
                const configSnap = await getDoc(doc(firestore, "config", "pricing"));
                if (configSnap.exists()) {
                    const data = configSnap.data();
                    if (data.gemini_cost_per_1m_tokens) {
                        setGeminiCost(data.gemini_cost_per_1m_tokens);
                    }
                }
            } catch (e) {
                console.warn("Could not load pricing config:", e);
            }

            // Load users
            const usersSnap = await getDocs(collection(firestore, "users"));
            const allUsers = usersSnap.docs.map((d) => d.data());

            // Load purchases
            const purchasesSnap = await getDocs(collection(firestore, "purchases"));
            const allPurchases = purchasesSnap.docs.map((d) => d.data());

            const now = new Date();
            const getRangeStart = (range: TimeRange) => {
                switch (range) {
                    case "24h": return new Date(now.getTime() - 24 * 60 * 60 * 1000);
                    case "7d": return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    case "30d": return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    case "90d": return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                    default: return new Date(0);
                }
            };

            const rangeStart = getRangeStart(timeRange);
            const prevRangeStart = new Date(rangeStart.getTime() - (now.getTime() - rangeStart.getTime()));

            // Filter data by range
            const filteredUsers = allUsers.filter(u => parseDate(u.first_seen) >= rangeStart);
            const prevFilteredUsers = allUsers.filter(u => {
                const seen = parseDate(u.first_seen);
                return seen >= prevRangeStart && seen < rangeStart;
            });

            const activeUsers = allUsers.filter(u => parseDate(u.last_seen) >= rangeStart).length;
            const prevActiveUsers = allUsers.filter(u => {
                const seen = parseDate(u.last_seen);
                return seen >= prevRangeStart && seen < rangeStart;
            }).length;

            const filteredPurchases = allPurchases.filter(p => parseDate(p.created_at) >= rangeStart);
            const prevFilteredPurchases = allPurchases.filter(p => {
                const created = parseDate(p.created_at);
                return created >= prevRangeStart && created < rangeStart;
            });

            const totalRevenue = filteredPurchases.reduce((sum, p) => sum + (p.stars_paid || 0), 0);
            const prevTotalRevenue = prevFilteredPurchases.reduce((sum, p) => sum + (p.stars_paid || 0), 0);

            // Detailed Business Analytics
            const uniquePayers = new Set(allPurchases.map(p => p.user_id)).size;
            const rangePayers = new Set(filteredPurchases.map(p => p.user_id)).size;
            const ltv = uniquePayers > 0 ? (allPurchases.reduce((sum, p) => sum + (p.stars_paid || 0), 0) / uniquePayers) : 0;
            const arppu = rangePayers > 0 ? (totalRevenue / rangePayers) : 0;

            const subscriptionRevenue = filteredPurchases
                .filter(p => p.type === "subscription")
                .reduce((sum, p) => sum + (p.stars_paid || 0), 0);
            const boosterRevenue = filteredPurchases
                .filter(p => p.type === "booster")
                .reduce((sum, p) => sum + (p.stars_paid || 0), 0);

            // Churn & Retention
            const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
            const churnRiskCount = allUsers.filter(u =>
                u.subscription_tier !== "free" &&
                u.subscription_tier !== undefined &&
                parseDate(u.last_seen) < fourteenDaysAgo
            ).length;

            const retentionEligible = allUsers.filter(u => {
                const ageDays = (now.getTime() - parseDate(u.first_seen).getTime()) / (1000 * 60 * 60 * 24);
                return ageDays >= 7;
            });
            const retainedUsers = retentionEligible.filter(u => {
                const first = parseDate(u.first_seen);
                const last = parseDate(u.last_seen);
                return (last.getTime() - first.getTime()) > (7 * 24 * 60 * 60 * 1000);
            }).length;
            const retentionRate = retentionEligible.length > 0 ? (retainedUsers / retentionEligible.length) * 100 : 0;

            // Conversion Time
            const userFirstSeenMap = new Map(allUsers.map(u => [u.user_id, parseDate(u.first_seen)]));
            const conversions = allPurchases.map(p => {
                const firstSeen = userFirstSeenMap.get(p.user_id);
                if (!firstSeen) return null;
                const purchaseDate = parseDate(p.created_at);
                return (purchaseDate.getTime() - firstSeen.getTime()) / (1000 * 60 * 60 * 24);
            }).filter((d): d is number => d !== null && d >= 0);
            const avgConversionDays = conversions.length > 0 ? conversions.reduce((a, b) => a + b, 0) / conversions.length : 0;

            // Trends
            const calculateTrend = (curr: number, prev: number) => {
                if (prev === 0) return curr > 0 ? 100 : 0;
                return Math.round(((curr - prev) / prev) * 100);
            };

            // User growth data for chart
            const growthPoints: Record<string, number> = {};
            allUsers.forEach(u => {
                const seenDate = parseDate(u.first_seen);
                const dateKey = seenDate.toLocaleDateString();
                if (seenDate >= rangeStart) {
                    growthPoints[dateKey] = (growthPoints[dateKey] || 0) + 1;
                }
            });

            // Revenue by item
            const revenueByItem: Record<string, { revenue: number; count: number }> = {};
            filteredPurchases.forEach((p) => {
                const item = p.item || "unknown";
                if (!revenueByItem[item]) {
                    revenueByItem[item] = { revenue: 0, count: 0 };
                }
                revenueByItem[item].revenue += p.stars_paid || 0;
                revenueByItem[item].count += 1;
            });

            // Load campaigns & promo codes
            let activeCampaigns = 0;
            let activePromoCodes = 0;
            try {
                const campaignsSnap = await getDocs(query(collection(firestore, "campaigns"), where("status", "==", "active")));
                activeCampaigns = campaignsSnap.size;
                const promoSnap = await getDocs(query(collection(firestore, "promo_codes"), where("is_active", "==", true)));
                activePromoCodes = promoSnap.size;
            } catch (e) { }

            // Load referrals
            let totalReferrals = 0;
            let completedReferrals = 0;
            let prevTotalReferrals = 0;
            let totalTextCreditsGiven = 0;
            let totalVoiceCreditsGiven = 0;
            try {
                const referralsSnap = await getDocs(collection(firestore, "referrals"));
                const allReferrals = referralsSnap.docs.map((d) => d.data());

                // Filter by time range
                const filteredReferrals = allReferrals.filter(r => parseDate(r.created_at) >= rangeStart);
                const prevFilteredReferrals = allReferrals.filter(r => {
                    const created = parseDate(r.created_at);
                    return created >= prevRangeStart && created < rangeStart;
                });

                totalReferrals = filteredReferrals.length;
                prevTotalReferrals = prevFilteredReferrals.length;
                completedReferrals = filteredReferrals.filter(r => r.referrer_reward_granted).length;

                // Sum up credits given
                filteredReferrals.forEach(r => {
                    if (r.referrer_reward_granted) {
                        totalTextCreditsGiven += (r.referrer_text_credits || 0) + (r.referred_text_credits || 0);
                        totalVoiceCreditsGiven += (r.referrer_voice_credits || 0) + (r.referred_voice_credits || 0);
                    }
                });
            } catch (e) {
                console.warn("Could not load referrals:", e);
            }

            // Load paywall events
            let paywallViews = 0;
            let paywallTapsUpgrade = 0;
            let paywallTapsBooster = 0;
            try {
                const eventsSnap = await getDocs(collection(firestore, "events"));
                const allEvents = eventsSnap.docs.map((d) => d.data());
                const filteredEvents = allEvents.filter(e => parseDate(e.created_at) >= rangeStart);

                paywallViews = filteredEvents.filter(e => e.event_type === "paywall_shown").length;
                paywallTapsUpgrade = filteredEvents.filter(e => e.event_type === "paywall_tapped_upgrade").length;
                paywallTapsBooster = filteredEvents.filter(e => e.event_type === "paywall_tapped_booster").length;
            } catch (e) {
                console.warn("Could not load events:", e);
            }

            const paywallConversionRate = paywallViews > 0 ? (filteredPurchases.length / paywallViews) * 100 : 0;

            setStats({
                totalUsers: allUsers.length,
                activeUsers,
                newUsers: filteredUsers.length,
                freeUsers: allUsers.filter(u => u.subscription_tier === "free" || !u.subscription_tier).length,
                proUsers: allUsers.filter(u => u.subscription_tier === "pro").length,
                premiumUsers: allUsers.filter(u => u.subscription_tier === "premium").length,
                totalPurchases: filteredPurchases.length,
                totalRevenue,
                activeCampaigns,
                activePromoCodes,
                totalTokensUsed: allUsers.reduce((sum, u) => sum + (u.token_usage || 0), 0),
                monthlyTokensUsed: allUsers.reduce((sum, u) => sum + (u.monthly_token_usage || 0), 0),
                totalReferrals,
                completedReferrals,
                totalTextCreditsGiven,
                totalVoiceCreditsGiven,
                ltv,
                arppu,
                rangePayersCount: rangePayers,
                subscriptionRevenue,
                boosterRevenue,
                retentionRate,
                churnRiskCount,
                avgConversionDays,
                paywallViews,
                paywallTapsUpgrade,
                paywallTapsBooster,
                paywallConversionRate,
                newUsersTrend: calculateTrend(filteredUsers.length, prevFilteredUsers.length),
                revenueTrend: calculateTrend(totalRevenue, prevTotalRevenue),
                activeUsersTrend: calculateTrend(activeUsers, prevActiveUsers),
                referralsTrend: calculateTrend(totalReferrals, prevTotalReferrals),
            });

            setGrowthData(Object.entries(growthPoints).map(([date, count]) => ({ date, count })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()));
            setRevenueData(Object.entries(revenueByItem).map(([name, data]) => ({
                name: name.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
                revenue: data.revenue,
                count: data.count,
            })));

        } catch (error) {
            console.error("Error loading dashboard data:", error);
        } finally {
            setLoading(false);
        }
    }

    function formatNumber(num: number): string {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
        if (num >= 1000) return (num / 1000).toFixed(1) + "K";
        return num.toString();
    }

    function exportToCSV() {
        if (!stats) return;

        const rows = [
            ["PapaqAI Analytics Export"],
            ["Time Range", timeRange.toUpperCase()],
            ["Export Date", new Date().toISOString()],
            [],
            ["Metric", "Value", "Trend (%)"],
            ["Active Users", stats.activeUsers, stats.activeUsersTrend],
            ["New Signups", stats.newUsers, stats.newUsersTrend],
            ["Total Users", stats.totalUsers, ""],
            ["Free Users", stats.freeUsers, ""],
            ["Pro Users", stats.proUsers, ""],
            ["Premium Users", stats.premiumUsers, ""],
            [],
            ["Revenue & Purchases"],
            ["Total Revenue (Stars)", stats.totalRevenue, stats.revenueTrend],
            ["Total Purchases", stats.totalPurchases, ""],
            [],
            ["Referral Program"],
            ["Total Referrals", stats.totalReferrals, stats.referralsTrend],
            ["Completed Referrals", stats.completedReferrals, ""],
            ["Text Credits Given", stats.totalTextCreditsGiven, ""],
            ["Voice Credits Given", stats.totalVoiceCreditsGiven, ""],
            [],
            ["Business Intelligence"],
            ["Customer LTV (Stars)", stats.ltv.toFixed(2), ""],
            ["ARPPU (Stars)", stats.arppu.toFixed(2), ""],
            ["Subscription Revenue (Stars)", stats.subscriptionRevenue, ""],
            ["Booster Revenue (Stars)", stats.boosterRevenue, ""],
            ["Avg Conversion (Days)", stats.avgConversionDays.toFixed(1), ""],
            ["Day-7 Retention Rate (%)", stats.retentionRate.toFixed(1), ""],
            ["Subscribers at Churn Risk", stats.churnRiskCount, ""],
            [],
            ["Paywall Conversion"],
            ["Paywall Views", stats.paywallViews, ""],
            ["Tapped Upgrade", stats.paywallTapsUpgrade, ""],
            ["Tapped Booster", stats.paywallTapsBooster, ""],
            ["Paywall Conversion Rate (%)", stats.paywallConversionRate.toFixed(1), ""],
            [],
            ["System Usage"],
            ["Monthly Token Usage", stats.monthlyTokensUsed, ""],
            ["Est. API Cost ($)", ((stats.monthlyTokensUsed / 1000000) * geminiCost).toFixed(2), ""],
            ["Active Campaigns", stats.activeCampaigns, ""],
            ["Active Promo Codes", stats.activePromoCodes, ""],
        ];

        const csvContent = rows.map(row => row.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `papaqai-analytics-${timeRange}-${new Date().toISOString().split("T")[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-pulse text-lg text-gray-400">Syncing analytics...</div>
            </div>
        );
    }

    if (!stats) return <div className="p-6 text-red-400">Stats not available.</div>;

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8 bg-gray-950 min-h-screen text-slate-200">
            <PageHeader
                title="PapaqAI Analytics"
                subtitle="Global overview and performance metrics"
                rightContent={
                    <div className="flex items-center gap-3">
                        <button
                            onClick={exportToCSV}
                            className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg border border-slate-700 transition-all flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Export CSV
                        </button>
                        <div className="flex bg-slate-900 p-1 rounded-xl shadow-inner border border-slate-800">
                            {(["24h", "7d", "30d", "90d", "all"] as TimeRange[]).map((r) => (
                                <button
                                    key={r}
                                    onClick={() => setTimeRange(r)}
                                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${timeRange === r
                                        ? "bg-blue-600 text-white shadow-lg"
                                        : "text-slate-400 hover:text-white"
                                        }`}
                                >
                                    {r.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>
                }
            />

            {/* Top KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Active Users"
                    value={stats.activeUsers}
                    trend={stats.activeUsersTrend}
                    icon="👥"
                    description="Users active in selected period"
                />
                <StatCard
                    title="New Signups"
                    value={stats.newUsers}
                    trend={stats.newUsersTrend}
                    icon="✨"
                    description="New registrations"
                />
                <StatCard
                    title="Revenue"
                    value={`${stats.totalRevenue} ⭐`}
                    trend={stats.revenueTrend}
                    icon="💰"
                    description="Total stars earned"
                    color="text-yellow-400"
                />
                <StatCard
                    title="Pro Users"
                    value={stats.proUsers + stats.premiumUsers}
                    icon="💎"
                    description="Total paying customers"
                    color="text-blue-400"
                />
            </div>

            {/* Business Intelligence & Traction */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-white">Unit Economics</h3>
                            <p className="text-slate-400 text-sm">Customer value and acquisition efficiency</p>
                        </div>
                        <span className="text-xs text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded-full uppercase">Profitability</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                            <p className="text-slate-400 text-xs mb-1 uppercase tracking-wider">LTV (Lifetime)</p>
                            <p className="text-2xl font-bold text-white">{stats.ltv.toFixed(0)} ⭐</p>
                            <p className="text-[10px] text-slate-500 mt-1">Avg revenue per payer</p>
                        </div>
                        <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                            <p className="text-slate-400 text-xs mb-1 uppercase tracking-wider">ARPPU ({timeRange})</p>
                            <p className="text-2xl font-bold text-blue-400">{stats.arppu.toFixed(0)} ⭐</p>
                            <p className="text-[10px] text-slate-500 mt-1">Revenue per active payer</p>
                        </div>
                        <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                            <p className="text-slate-400 text-xs mb-1 uppercase tracking-wider">Avg Conversion</p>
                            <p className="text-2xl font-bold text-emerald-400">{stats.avgConversionDays.toFixed(1)} Days</p>
                            <p className="text-[10px] text-slate-500 mt-1">Join → First purchase</p>
                        </div>
                        <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                            <p className="text-slate-400 text-xs mb-1 uppercase tracking-wider">Payers</p>
                            <p className="text-2xl font-bold text-white">{stats.rangePayersCount}</p>
                            <p className="text-[10px] text-slate-500 mt-1">Unique customers in period</p>
                        </div>
                    </div>
                </div>

                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-white">Revenue Concentration</h3>
                            <p className="text-slate-400 text-sm">Subscription vs. Booster refills</p>
                        </div>
                    </div>
                    <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: "Subscriptions", value: stats.subscriptionRevenue },
                                        { name: "Booster Packs", value: stats.boosterRevenue },
                                    ]}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    <Cell fill="#8b5cf6" />
                                    <Cell fill="#3b82f6" />
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: "#0f172a", border: "none", borderRadius: "8px" }} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Referral Stats */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-white">Referral Program</h3>
                        <p className="text-slate-400 text-sm">User growth through invites</p>
                    </div>
                    <span className="text-xs text-pink-500 bg-pink-500/10 px-2 py-0.5 rounded-full uppercase">Viral Growth</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-slate-400 text-xs uppercase tracking-wider">Total Referrals</p>
                            {stats.referralsTrend !== 0 && (
                                <span className={`text-xs font-bold ${stats.referralsTrend >= 0 ? "text-green-500" : "text-red-500"}`}>
                                    {stats.referralsTrend >= 0 ? "↑" : "↓"} {Math.abs(stats.referralsTrend)}%
                                </span>
                            )}
                        </div>
                        <p className="text-2xl font-bold text-pink-400">{stats.totalReferrals}</p>
                    </div>
                    <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                        <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider">Completed</p>
                        <p className="text-2xl font-bold text-green-400">{stats.completedReferrals}</p>
                        <p className="text-xs text-slate-500 mt-1">
                            {stats.totalReferrals > 0 ? ((stats.completedReferrals / stats.totalReferrals) * 100).toFixed(0) : 0}% conversion
                        </p>
                    </div>
                    <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                        <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider">Text Credits Given</p>
                        <p className="text-2xl font-bold text-emerald-400">+{stats.totalTextCreditsGiven}</p>
                    </div>
                    <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                        <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider">Voice Credits Given</p>
                        <p className="text-2xl font-bold text-amber-400">+{stats.totalVoiceCreditsGiven}</p>
                    </div>
                </div>
            </div>

            {/* Paywall Conversion Funnel */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-white">Paywall Conversion</h3>
                        <p className="text-slate-400 text-sm">Quota-exceeded to purchase funnel</p>
                    </div>
                    <span className="text-xs text-orange-500 bg-orange-500/10 px-2 py-0.5 rounded-full uppercase">Monetization</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                        <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider">Paywall Views</p>
                        <p className="text-2xl font-bold text-orange-400">{stats.paywallViews}</p>
                        <p className="text-[10px] text-slate-500 mt-1">Times quota exceeded</p>
                    </div>
                    <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                        <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider">Tapped Upgrade</p>
                        <p className="text-2xl font-bold text-purple-400">{stats.paywallTapsUpgrade}</p>
                        <p className="text-xs text-slate-500 mt-1">
                            {stats.paywallViews > 0 ? ((stats.paywallTapsUpgrade / stats.paywallViews) * 100).toFixed(0) : 0}% tap rate
                        </p>
                    </div>
                    <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                        <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider">Tapped Booster</p>
                        <p className="text-2xl font-bold text-blue-400">{stats.paywallTapsBooster}</p>
                        <p className="text-xs text-slate-500 mt-1">
                            {stats.paywallViews > 0 ? ((stats.paywallTapsBooster / stats.paywallViews) * 100).toFixed(0) : 0}% tap rate
                        </p>
                    </div>
                    <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 border-green-500/30">
                        <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider">Conversion Rate</p>
                        <p className="text-2xl font-bold text-green-400">{stats.paywallConversionRate.toFixed(1)}%</p>
                        <p className="text-[10px] text-slate-500 mt-1">Paywall → Purchase</p>
                    </div>
                </div>
            </div>

            {/* Retention & Health */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-white">Stickiness & Churn Risk</h3>
                        <p className="text-slate-400 text-sm">Product-market fit indicators</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-slate-500 uppercase tracking-widest">Platform Health</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                        <div className="flex justify-between items-baseline">
                            <h4 className="text-sm font-semibold text-slate-300">Cohort Retention (Day 7)</h4>
                            <span className="text-2xl font-bold text-emerald-400">{stats.retentionRate.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-slate-800 h-3 rounded-full overflow-hidden">
                            <div className="bg-emerald-500 h-full transition-all duration-1000" style={{ width: `${stats.retentionRate}%` }} />
                        </div>
                        <p className="text-xs text-slate-500 italic">Percentage of users who stay active at least one week after signing up.</p>
                    </div>
                    <div className="flex items-center gap-6 p-4 bg-red-500/5 border border-red-500/10 rounded-2xl">
                        <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center text-2xl text-red-500">
                            🚨
                        </div>
                        <div>
                            <p className="text-slate-400 text-xs uppercase font-bold tracking-wider">Churn Risk (Paying Users)</p>
                            <p className="text-2xl font-bold text-white">{stats.churnRiskCount} <span className="text-sm font-normal text-slate-500">Subscribers</span></p>
                            <p className="text-[10px] text-red-400/60 mt-0.5">Paid users hasn't been seen in &gt;14 days</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Growth Chart */}
                <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-white">Daily Acquisition</h3>
                        <div className="text-xs text-slate-500 uppercase tracking-widest">New Users / Day</div>
                    </div>
                    <div className="h-[350px] w-100%">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={growthData}>
                                <defs>
                                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" />
                                <XAxis
                                    dataKey="date"
                                    stroke="#64748b"
                                    fontSize={11}
                                    tickFormatter={(val) => val.split("/").slice(0, 2).join("/")}
                                />
                                <YAxis stroke="#64748b" fontSize={11} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #1e293b", borderRadius: "12px" }}
                                />
                                <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorCount)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Conversion Section */}
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                    <h3 className="text-lg font-bold text-white mb-6">Subscription Mix</h3>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: "Free", value: stats.freeUsers },
                                        { name: "Pro", value: stats.proUsers },
                                        { name: "Premium", value: stats.premiumUsers },
                                    ]}
                                    innerRadius={70}
                                    outerRadius={100}
                                    paddingAngle={8}
                                    dataKey="value"
                                >
                                    <Cell fill={COLORS.free} />
                                    <Cell fill={COLORS.pro} />
                                    <Cell fill={COLORS.premium} />
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: "#0f172a", border: "none", borderRadius: "8px" }} />
                                <Legend verticalAlign="bottom" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="mt-6 space-y-4">
                        <ConversionRow label="Free to Paid" percent={stats.totalUsers > 0 ? ((stats.proUsers + stats.premiumUsers) / stats.totalUsers * 100).toFixed(1) : "0"} color="bg-green-500" />
                        <ConversionRow label="Pro to Premium" percent={stats.proUsers > 0 ? (stats.premiumUsers / stats.proUsers * 100).toFixed(1) : "0"} color="bg-purple-500" />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Revenue Breakdown */}
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-white">Top Products</h3>
                        <span className="text-xs text-yellow-500 bg-yellow-500/10 px-2 py-0.5 rounded-full uppercase">Stars Earned</span>
                    </div>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={revenueData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#1e293b" />
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={12} width={100} />
                                <Tooltip cursor={{ fill: '#1e293b' }} contentStyle={{ backgroundColor: "#0f172a", border: "none", borderRadius: "8px" }} />
                                <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* System Costs & Usage */}
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                    <h3 className="text-lg font-bold text-white mb-6">System Health & Usage</h3>
                    <div className="space-y-6">
                        <div className="flex items-center justify-between p-4 bg-slate-950 rounded-2xl border border-slate-800">
                            <div>
                                <p className="text-slate-400 text-sm">Monthly Token Usage</p>
                                <p className="text-2xl font-bold text-white">{formatNumber(stats.monthlyTokensUsed)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-slate-400 text-sm">Est. API Cost</p>
                                <p className="text-2xl font-bold text-green-400">${((stats.monthlyTokensUsed / 1000000) * geminiCost).toFixed(2)}</p>
                                <p className="text-[10px] text-slate-500">@ ${geminiCost}/1M tokens</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                                <p className="text-slate-400 text-xs mb-1 uppercase tracking-wider">Active Campaigns</p>
                                <p className="text-2xl font-bold text-orange-400">{stats.activeCampaigns}</p>
                            </div>
                            <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800">
                                <p className="text-slate-400 text-xs mb-1 uppercase tracking-wider">Promo Codes</p>
                                <p className="text-2xl font-bold text-pink-400">{stats.activePromoCodes}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatCard({ title, value, trend, icon, description, color = "text-white" }: any) {
    return (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-md hover:shadow-xl transition-shadow group">
            <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                    {icon}
                </div>
                {trend !== undefined && (
                    <div className={`flex items-center text-sm font-bold ${trend >= 0 ? "text-green-500" : "text-red-500"}`}>
                        {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%
                    </div>
                )}
            </div>
            <div>
                <p className={`text-3xl font-black ${color}`}>{typeof value === "number" ? value.toLocaleString() : value}</p>
                <p className="text-white font-semibold text-sm mt-1">{title}</p>
                <p className="text-slate-500 text-xs mt-0.5">{description}</p>
            </div>
        </div>
    );
}

function ConversionRow({ label, percent, color }: any) {
    return (
        <div>
            <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-400">{label}</span>
                <span className="text-white font-bold">{percent}%</span>
            </div>
            <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div className={`${color} h-full transition-all duration-1000`} style={{ width: `${percent}%` }} />
            </div>
        </div>
    );
}
